/**
 * Shared formatting utilities for currency, dates, etc.
 */

/**
 * Format an amount in INR with Cr/Lakh shorthand.
 * Expects amount in base units (rupees, not paisa).
 */
export function formatPrice(amount: number): string {
	// The sign goes in front of the rupee symbol and the magnitude keeps its
	// Lakh/Cr suffix. An escrow balance can be negative (`getProjectPayments`).
	if (amount < 0) return `-${formatPrice(-amount)}`;
	if (amount >= 10000000) {
		return `₹${(amount / 10000000).toFixed(1)} Cr`;
	}
	if (amount >= 100000) {
		return `₹${(amount / 100000).toFixed(0)} Lakh`;
	}
	return `₹${amount.toLocaleString("en-IN")}`;
}

/**
 * Format an amount in whole rupees with Indian digit grouping and no Lakh/Cr
 * rounding, for places where lines have to add up to a total.
 * Expects amount in base units (rupees, not paisa).
 */
export function formatRupees(amount: number): string {
	// Adding 0 turns the -0 that rounding a small negative gives into 0.
	const rupees = Math.round(amount) + 0;
	if (rupees < 0) return `-${formatRupees(-rupees)}`;
	return `₹${rupees.toLocaleString("en-IN")}`;
}

/**
 * Format an amount in INR using Indian notation with Lakh/Cr suffixes.
 * LAKH(n) → ₹x.xxL (≥1,00,000) or ₹x.xx Cr (≥1,00,00,000)
 * Expects amount in base units (rupees).
 */
export function LAKH(amount: number): string {
	if (amount >= 10000000) {
		return `₹${(amount / 10000000).toFixed(2)} Cr`;
	}
	if (amount >= 100000) {
		return `₹${(amount / 100000).toFixed(2)} L`;
	}
	return `₹${amount.toLocaleString("en-IN")}`;
}

/**
 * Format a budget range (min - max) in INR.
 */
export function formatBudgetRange(
	min: number | null,
	max: number | null,
): string {
	if (min && max) return `${formatPrice(min)} - ${formatPrice(max)}`;
	if (max) return formatPrice(max);
	if (min) return `From ${formatPrice(min)}`;
	return "Budget not set";
}

/**
 * Format an amount in paisa to INR display string.
 * For catalog prices stored in paisa (1/100th of a rupee).
 */
export function formatPricePaisa(paisa: number): string {
	return `₹${(paisa / 100).toLocaleString("en-IN")}`;
}

/**
 * A paisa amount as the text of a rupee input: 4800000 becomes "48000".
 * The BOQ price fields take rupees, because that is what a designer types.
 */
export function paisaToRupeeInput(paisa: number): string {
	return String(paisa / 100);
}

/**
 * The paisa amount for what was typed into a rupee input, rounded to a whole
 * paisa. NaN when the text is not a number, as `parseInt` gave before.
 */
export function rupeeInputToPaisa(rupees: string): number {
	return Math.round(Number.parseFloat(rupees) * 100);
}

// ---------------------------------------------------------------------------
// Calendar dates
// ---------------------------------------------------------------------------

/**
 * Format a CALENDAR DATE for display, without letting the reader's timezone
 * move it.
 *
 * Some of our date fields name a day, not a moment: a milestone's `targetDate`
 * is a deadline someone agreed to, and a price list's effective date is a day
 * printed on a PDF. They arrive over JSON as ISO instants at midnight UTC
 * ("2024-04-10T00:00:00.000Z") because that is the only shape the wire has, but
 * the instant is an artefact of the encoding -- nobody chose midnight.
 *
 * `new Date(...).toLocaleDateString()` renders in the reader's zone, which
 * moves that day backwards for anyone west of UTC: at UTC-7 the deadline above
 * displays as 9 April. This is not cosmetic. A homeowner in California and a
 * designer in Bengaluru reading the same milestone see different due dates, and
 * the milestone is late a day earlier for one of them than the other.
 *
 * So the day is read in UTC, the zone it was written in.
 *
 * Use this ONLY for fields that denote a day, such as a price's `observedAt`
 * (its effective date). A true instant -- a message timestamp, an audit time --
 * must keep rendering in local time, because for those the reader's own clock
 * IS the right frame.
 *
 * @param value ISO 8601 date or date-time string.
 * @param locale BCP 47 tag; defaults to en-IN, our primary market.
 */
export function formatCalendarDate(
	value: string,
	options: Intl.DateTimeFormatOptions = {},
	locale = "en-IN",
): string {
	return new Date(value).toLocaleDateString(locale, {
		...options,
		timeZone: "UTC",
	});
}

/**
 * Title-case each whitespace-separated token (e.g. "new delhi" → "New Delhi").
 * Used to canonicalize free-text location entries before they hit a list of
 * pre-canonicalized options, so `serviceCities.includes(...)` lookups are
 * case-stable.
 */
export function titleCase(value: string): string {
	return value
		.trim()
		.split(/\s+/)
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");
}
